import React from 'react';
import { CombustionChamberProps } from '../types';

const FuelSpray: React.FC<{ isInjecting: boolean; isCombusting: boolean; pressure: number }> = ({ isInjecting, isCombusting, pressure }) => {
  const baseOpacity = isInjecting ? Math.min(0.5 + (pressure - 800) / 1700 * 0.5, 1) : 0;
  const sprayOpacity = isCombusting ? baseOpacity * 0.15 : baseOpacity;

  const sprayScaleY = isInjecting ? 1 : 0;
  // Make transitions faster to be visible in the slowed-down time
  const sprayTransitionDuration = isInjecting ? `75ms` : `150ms`;
  const sprayWidth = 20 + ((pressure - 800) / 1700) * 150;
  const sprayHeight = 150 + ((pressure - 800) / 1700) * 150;

  return (
    <div id="Cont_FuelSpray"
      className="absolute top-0 left-1/2 -translate-x-1/2 w-full origin-top z-10"
      style={{
        transform: `scaleY(${sprayScaleY})`,
        opacity: sprayOpacity,
        transition: `transform ${sprayTransitionDuration} ease-out, opacity 100ms ease-out`,
        height: `${sprayHeight}px`,
        left:'0',
      }}>
      <div 
        className="w-full h-full absolute top-0"
        style={{
          boxShadow: `
            0 20px 40px rgba(135, 206, 250, ${sprayOpacity * 0.8}),
            0 0 10px rgba(200, 230, 255, ${sprayOpacity * 0.6}),
            0 0 20px rgba(200, 230, 255, ${sprayOpacity * 0.4})
          `,
        }}
      >
        <div id="FuelSpray"
          className="h-full bg-gradient-to-t from-cyan-200/50 via-white/70 to-transparent"
          style={{ 
            clipPath: `polygon(45% 0, 55% 0, 100% 100%, 0% 100%)`,
            width: `${sprayWidth}px`,
            marginLeft: `-${sprayWidth / 2}px`,
            left: '50%',
             transform: 'translateX(-50%)',
          }}
        ></div>
      </div>
    </div>
  );
};

const Combustion: React.FC<{ isCombusting: boolean; pressure: number; temperature: number }> = ({ isCombusting, pressure, temperature }) => {
  const pressureIntensity = (pressure - 800) / (2500 - 800);
  const scale = 0.8 + pressureIntensity * 0.4;
  const opacity = isCombusting ? 1 : 0;

  // Map temperature (500-2000°C) to a color gradient
  const tempRatio = Math.max(0, Math.min(1, (temperature - 500) / (2000 - 500))); // Normalized 0 to 1

  // Center color: from yellow-orange to bright white
  const centerR = 255;
  const centerG = 220 + tempRatio * 35; // 220 -> 255
  const centerB = 150 + tempRatio * 105; // 150 -> 255
  const centerColor = `rgb(${centerR}, ${centerG}, ${centerB})`;

  // Mid color: from deep orange to yellow-orange
  const midR = 255;
  const midG = 140 + tempRatio * 80; // 140 -> 220
  const midB = 0 + tempRatio * 50;   // 0 -> 50
  const midColor = `rgb(${midR}, ${midG}, ${midB})`;

  return (
    <div
      className="absolute -translate-y-1/2 w-48 h-48 transition-opacity pointer-events-none z-20"
      style={{
        opacity,
        transform: `scale(${isCombusting ? scale + (Math.random() * 0.1 - 0.05) : 0.5})`,
        transition: isCombusting ? 'transform 50ms linear' : 'opacity 100ms ease-out, transform 150ms cubic-bezier(0.5, 2, 0.5, 1)',
        top: '-60px', // Lowered to be more central in the chamber
        left:'11%'
      }}
    >
      <div 
        className="absolute inset-0 rounded-full"
        style={{
          background: `radial-gradient(circle, ${centerColor} 10%, ${midColor} 40%, transparent 70%)`,
          opacity: isCombusting ? 0.9 : 0,
          filter: `blur(15px) brightness(1.2)`,
        }}
      />
       <div 
        className="absolute inset-4 rounded-full"
        style={{
          background: `radial-gradient(circle, white, ${centerColor} 60%, transparent 80%)`,
          opacity: isCombusting ? 1 : 0,
          filter: `blur(5px)`,
        }}
      />
    </div>
  );
};


const PISTON_TRAVEL = 220; // corsa in px
// Geometria: camera h-80 (320px) con bordo superiore 8px => area interna 312px.
// Il pistone è alto 48px (h-12) con bottom -30px.
const CHAMBER_INNER_HEIGHT = 312;
const PISTON_HEIGHT = 48;
const PISTON_BOTTOM_OFFSET = -30;

// Spostamento verso l'alto del pistone rispetto al punto morto inferiore, in px.
// 0/360 = TDC (Top Dead Center), 180 = BDC (Bottom Dead Center)
const getPistonLift = (enginePhase: number): number => {
    const radians = enginePhase * (Math.PI / 180);
    // Moto liscio su e giù tramite il seno.
    const yPos = PISTON_TRAVEL * Math.abs(Math.sin(radians));
    return PISTON_TRAVEL - yPos;
};

// Quota (dall'alto della camera) della testa del pistone.
const getPistonTop = (enginePhase: number): number =>
    CHAMBER_INNER_HEIGHT - PISTON_BOTTOM_OFFSET - PISTON_HEIGHT - getPistonLift(enginePhase);

const Piston: React.FC<{ enginePhase: number; isRunning: boolean }> = ({ enginePhase, isRunning }) => {
    const finalTranslateY = getPistonLift(enginePhase);

    return (
        <div
            className="absolute bottom-[-30px] left-0 right-0 mx-auto w-[90%] h-12 z-0"
            style={{
                transform: `translateY(-${finalTranslateY}px)`,
                transition: isRunning ? 'none' : 'transform 10ms linear',
            }}
        >
            <div className="w-full h-full bg-gradient-to-t from-slate-600 to-slate-500 rounded-md border-t-4 border-slate-400 shadow-lg">
                <div className="w-full h-full bg-black/10 rounded-md"></div>
            </div>
        </div>
    );
}

const CombustionChamber: React.FC<CombustionChamberProps> = ({
  isRunning, isInjecting, isCombusting, pressure, isFlameViewActive, temperature, enginePhase
}) => {

  if (isFlameViewActive) {
    return (
      <div className="relative w-64 h-80 bg-slate-800 border-x-8 border-t-8 border-slate-700 rounded-t-3xl overflow-hidden shadow-inner flex items-center justify-center">
        <div className="absolute inset-0 rounded-t-3xl shadow-[inset_0_-10px_30px_rgba(0,0,0,0.6)]"></div>
        <Combustion isCombusting={true} pressure={pressure} temperature={temperature} />
      </div>
    );
  }

  return (
    <div className="relative w-64 h-80 bg-slate-800 border-x-8 border-t-8 border-slate-700 rounded-t-3xl overflow-hidden shadow-inner">
      <div className="absolute inset-0 rounded-t-3xl shadow-[inset_0_-10px_30px_rgba(0,0,0,0.6)]"></div>
      
      {/* Spray e fiamma sono confinati sopra la testa del pistone: non lo attraversano né lo ricoprono */}
      <div
        className="absolute left-0 right-0 top-0 overflow-hidden z-10 pointer-events-none"
        style={{ height: `${Math.max(0, getPistonTop(enginePhase))}px` }}
      >
        <FuelSpray isInjecting={isInjecting} isCombusting={isCombusting} pressure={pressure} />
        <Combustion isCombusting={isCombusting} pressure={pressure} temperature={temperature} />
      </div>

      <Piston enginePhase={enginePhase} isRunning={isRunning} />
    </div>
  );
};

export default CombustionChamber;